import React, { useState, useMemo } from "react";
import type { MenuItem } from "../../data/menuData";
import type { AdminData } from "./AdminLayout";

interface Props {
  data: AdminData;
  updateData: (updater: (prev: AdminData) => AdminData) => void;
}

const LOCATIONS = ["montreal", "delson"];

type FlagKey = "popular" | "spicy" | "vegetarian" | "vegan" | "glutenFree" | "new";

const BulkOperations: React.FC<Props> = ({ data, updateData }) => {
  const [mode, setMode] = useState<"price" | "flags">("price");
  const [priceMode, setPriceMode] = useState<"percent" | "fixed">("percent");
  const [priceValue, setPriceValue] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterSubmenu, setFilterSubmenu] = useState("all");
  const [flag, setFlag] = useState<FlagKey>("popular");
  const [flagValue, setFlagValue] = useState(true);
  const [applied, setApplied] = useState(false);

  const availableSubmenus = useMemo(() => {
    const subs = new Set<string>();
    for (const item of data.menuItems) {
      if (filterLocation !== "all" && item.location !== filterLocation)
        continue;
      if (item.submenu) subs.add(item.submenu);
    }
    return Array.from(subs).sort();
  }, [data.menuItems, filterLocation]);

  const targetItems = useMemo(() => {
    return data.menuItems.filter((item) => {
      if (filterLocation !== "all" && item.location !== filterLocation)
        return false;
      if (filterSubmenu !== "all" && item.submenu !== filterSubmenu)
        return false;
      return true;
    });
  }, [data.menuItems, filterLocation, filterSubmenu]);

  const getItemName = (item: MenuItem): string => {
    if (item.name) return item.name;
    if (item.recipeId && data.recipes[item.recipeId]) {
      return data.recipes[item.recipeId].name;
    }
    return item.id;
  };

  const getSubmenuLabel = (submenu: string): string => {
    const label = data.submenuLabels[submenu];
    return label ? label.en : submenu;
  };

  const pricePreview = useMemo(() => {
    const val = parseFloat(priceValue);
    if (isNaN(val) || val === 0) return [];

    return targetItems.map((item) => {
      let newPrice: number;
      if (priceMode === "percent") {
        newPrice = Math.round(item.price * (1 + val / 100) * 100) / 100;
      } else {
        newPrice = Math.round((item.price + val) * 100) / 100;
      }
      return {
        item,
        oldPrice: item.price,
        newPrice: Math.max(0.01, newPrice),
      };
    });
  }, [targetItems, priceMode, priceValue]);

  const applyPriceChanges = () => {
    if (pricePreview.length === 0) return;

    const priceMap = new Map<string, number>();
    for (const change of pricePreview) {
      priceMap.set(change.item.id, change.newPrice);
    }

    updateData((prev) => ({
      ...prev,
      menuItems: prev.menuItems.map((item) => {
        const newPrice = priceMap.get(item.id);
        if (newPrice !== undefined) {
          return { ...item, price: newPrice };
        }
        return item;
      }),
    }));

    setApplied(true);
    setTimeout(() => setApplied(false), 2000);
  };

  const applyFlagChanges = () => {
    const targetIds = new Set(targetItems.map((i) => i.id));

    updateData((prev) => ({
      ...prev,
      menuItems: prev.menuItems.map((item) => {
        if (!targetIds.has(item.id)) return item;
        const updated = { ...item };
        if (flagValue) {
          updated[flag] = true;
        } else {
          delete updated[flag];
        }
        return updated;
      }),
    }));

    setApplied(true);
    setTimeout(() => setApplied(false), 2000);
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Bulk Operations</h2>
      </div>

      <div className="admin-toolbar">
        <button
          className={`btn ${mode === "price" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setMode("price")}
        >
          Price Update
        </button>
        <button
          className={`btn ${mode === "flags" ? "btn-primary" : "btn-secondary"}`}
          onClick={() => setMode("flags")}
        >
          Toggle Flags
        </button>
      </div>

      {/* Scope filter */}
      <div className="admin-toolbar">
        <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Scope:</label>
        <select
          value={filterLocation}
          onChange={(e) => {
            setFilterLocation(e.target.value);
            setFilterSubmenu("all");
          }}
        >
          <option value="all">All Locations</option>
          {LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>
              {loc.charAt(0).toUpperCase() + loc.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={filterSubmenu}
          onChange={(e) => setFilterSubmenu(e.target.value)}
        >
          <option value="all">All Submenus</option>
          {availableSubmenus.map((sub) => (
            <option key={sub} value={sub}>
              {getSubmenuLabel(sub)}
            </option>
          ))}
        </select>
        <span className="admin-item-count">
          {targetItems.length} items selected
        </span>
      </div>

      {mode === "price" && (
        <>
          <div className="admin-toolbar">
            <select
              value={priceMode}
              onChange={(e) =>
                setPriceMode(e.target.value as "percent" | "fixed")
              }
            >
              <option value="percent">By Percentage (%)</option>
              <option value="fixed">By Fixed Amount ($)</option>
            </select>
            <input
              type="number"
              step={priceMode === "percent" ? "1" : "0.01"}
              placeholder={
                priceMode === "percent" ? "e.g. 5 or -3" : "e.g. 1.00 or -0.50"
              }
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              style={{ width: "120px" }}
            />
            <button
              className="btn btn-success"
              onClick={applyPriceChanges}
              disabled={pricePreview.length === 0}
            >
              Apply
            </button>
            {applied && (
              <span style={{ color: "#16a34a", fontWeight: 600 }}>
                Applied!
              </span>
            )}
          </div>

          {pricePreview.length > 0 && (
            <div className="admin-bulk-preview">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Old Price</th>
                    <th>New Price</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {pricePreview.map(({ item, oldPrice, newPrice }) => (
                    <tr key={item.id}>
                      <td>{getItemName(item)}</td>
                      <td>${oldPrice.toFixed(2)}</td>
                      <td>
                        <strong>${newPrice.toFixed(2)}</strong>
                      </td>
                      <td
                        style={{
                          color:
                            newPrice > oldPrice
                              ? "#dc2626"
                              : newPrice < oldPrice
                                ? "#16a34a"
                                : "#6b7280",
                        }}
                      >
                        {newPrice > oldPrice ? "+" : ""}$
                        {(newPrice - oldPrice).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {mode === "flags" && (
        <>
          <div className="admin-toolbar">
            <select value={flag} onChange={(e) => setFlag(e.target.value as FlagKey)}>
              <option value="popular">Popular</option>
              <option value="new">New</option>
              <option value="spicy">Spicy</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="glutenFree">Gluten Free</option>
            </select>
            <select
              value={flagValue.toString()}
              onChange={(e) => setFlagValue(e.target.value === "true")}
            >
              <option value="true">Set to TRUE</option>
              <option value="false">Remove flag</option>
            </select>
            <button
              className="btn btn-success"
              onClick={applyFlagChanges}
              disabled={targetItems.length === 0}
            >
              Apply to {targetItems.length} items
            </button>
            {applied && (
              <span style={{ color: "#16a34a", fontWeight: 600 }}>
                Applied!
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BulkOperations;
